import { ChangeType, DomainChange, TrackableObject } from './DomainChange';
import { domainTrace } from '../diagnostics/domainTrace';

export class ChangeTracker {
    private pendingChanges = new Map<TrackableObject, ChangeType>();
    private batchDepth = 0;

    constructor(private readonly onFlush: (changes: DomainChange[]) => void) {}

    markChanged(obj: TrackableObject, change: ChangeType = 'modified'): void {
        if (this.batchDepth === 0) {
            throw new Error(
                'Mutation outside command()/batch(). ' +
                    'Wrap in tracker.command() or tracker.batch() ' +
                    '(e.g. context.tracker.command(() => ...)).'
            );
        }
        // 'created' / 'deleted' should win over 'modified'; otherwise keep latest.
        const existing = this.pendingChanges.get(obj);
        if (existing === 'created') {
            if (change === 'deleted') {
                // Net effect of create-then-delete inside one batch is
                // nothing: subscribers never saw the object exist, so
                // emitting either entry would be a phantom.
                this.pendingChanges.delete(obj);
            }
            return;
        }
        if (existing === 'deleted') {
            return;
        }
        this.pendingChanges.set(obj, change);
    }

    command<T>(operation: () => T): T {
        // Snapshot the pending set on the way in so we can roll back to
        // it if the operation throws mid-batch. Without this, a partial
        // mutation that fails halfway through would still be emitted as
        // a real changeset — risking a corrupt or half-applied clinical
        // state being persisted / replayed.
        const outermost = this.batchDepth === 0;
        const snapshot = outermost ? null : new Map(this.pendingChanges);
        this.batchDepth++;
        let result: T;
        try {
            result = operation();
        } catch (error) {
            // Roll back: at the outermost call clear the whole pending
            // set (nothing committed yet); inside a nested command,
            // restore the parent's pending set.
            this.batchDepth--;
            if (outermost) {
                this.pendingChanges.clear();
            } else if (snapshot) {
                this.pendingChanges = snapshot;
            }
            throw error;
        }
        // Depth bookkeeping completes BEFORE flush so a throwing
        // changeset listener cannot decrement twice (1 → 0 → -1) and
        // silently freeze the tracker for the rest of the session.
        // flush() clears the pending set before invoking listeners, so
        // the tracker is consistent even if a listener throws here.
        this.batchDepth--;
        if (this.batchDepth === 0) {
            this.flush();
        }
        return result;
    }

    batch<T>(operation: () => T): T {
        return this.command(operation);
    }

    private flush(): void {
        if (this.pendingChanges.size === 0) {
            return;
        }
        domainTrace.tracker('flush', {
            count: this.pendingChanges.size,
            kinds: Array.from(this.pendingChanges).map(
                ([obj, type]) => `${obj.trackableKind}:${type}`
            ),
        });
        const changes: DomainChange[] = [];
        for (const [obj, type] of this.pendingChanges) {
            const snapshot = obj.captureSnapshot();
            changes.push({
                kind: obj.trackableKind,
                id: obj.trackableId,
                type,
                snapshot,
            } as DomainChange);
        }
        this.pendingChanges.clear();
        this.onFlush(changes);
    }
}
